<template>
  <div class="waybill-query-page">
    <!-- 搜索区域 -->
    <div class="search-section">
      <el-form :model="form" inline class="search-form">
        <el-form-item label="运单号">
          <el-input
            v-model="form.waybillNo"
            type="textarea"
            :rows="4"
            resize="none"
            placeholder="请输入运单号，每行一条，最多50条"
            style="width: 320px;"
          />
        </el-form-item>
        <el-form-item label="快递公司">
          <el-select v-model="form.cpCode" placeholder="请选择快递公司" clearable style="width: 180px;">
            <el-option v-for="item in cpList" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item class="action-item">
          <div class="action-group">
            <el-checkbox v-model="isDataItemOpen">展开所有</el-checkbox>
            <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleBatchQuery">
              查询轨迹
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <!-- 结果展示区域 -->
    <div class="result-section">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-wrapper">
        <i class="el-icon-loading"></i> 正在查询中，请稍候...
      </div>

      <!-- 折叠面板 -->
      <el-collapse v-else-if="data.length > 0" v-model="activeNames" class="waybill-collapse">
        <el-collapse-item v-for="(item, index) in data" :key="index" :name="String(index)">
          <template #title>
            <div class="collapse-title">
              <el-avatar :size="24" :src="global.expressLogoUrl + item.cpCode + '.png'" />
              <span class="cp-name">{{ item.cpName }}</span>
              <span class="waybill-no">{{ item.waybillNo }}</span>
              
              <!-- 动态状态标签 -->
              <el-tag 
                :type="getStatusType(item.route)" 
                effect="dark" 
                size="small"
                class="status-tag"
              >
                {{ getStatusText(item.route) }}
              </el-tag>
              
              <el-tag size="small" type="info">
                全程耗时：{{ timeConsuming(item.route) }} 小时
              </el-tag>
            </div>
          </template>

          <!-- 轨迹表格 -->
          <el-table 
            v-if="item.route && item.route.length" 
            :data="item.route" 
            stripe 
            size="small"
            style="width: 100%;"
          >
            <el-table-column prop="time" label="操作时间" width="180" />
            <el-table-column prop="text" label="物流信息">
              <template #default="scope">
                <span>{{ scope.row.text }}</span>
                <span v-if="scope.row.weight" class="weight-tag">
                  ({{ scope.row.weight }}kg)
                </span>
              </template>
            </el-table-column>
          </el-table>
          
          <el-empty v-else description="暂无物流轨迹" image-size="60" />
        </el-collapse-item>
      </el-collapse>

      <!-- 空状态 -->
      <el-empty v-else-if="!loading" description="暂无查询记录，请输入运单号查询" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'WaybillQuery',
  data() {
    return {
      loading: false,
      form: {
        waybillNo: '',
        cpCode: '',
        phone: ''
      },
      cpList: [
        { value: '', label: '自动识别' },
        { value: 'ZTO', label: '中通快递' },
        { value: 'YTO', label: '圆通速递' },
        { value: 'STO', label: '申通快递' },
        { value: 'YD', label: '韵达速递' },
        { value: 'JT', label: '极兔速递' },
        { value: 'POST', label: '中国邮政(含EMS)' },
        { value: 'SF', label: '顺丰速运' }
      ],
      data: [],
      activeNames: [],
      isDataItemOpen: false
    };
  },

  methods: {
    // 解析运单号列表
    parseWaybillNos(text) {
      if (!text) return [];
      return text
        .trim()
        .split('\n')
        .map(line => line.replace(/[^a-zA-Z0-9]/g, ''))
        .filter(Boolean)
        .slice(0, 50); // 最多50条
    },

    // 批量查询入口
    async handleBatchQuery() {
      const waybillList = this.parseWaybillNos(this.form.waybillNo);
      
      if (waybillList.length === 0) {
        this.$message.warning('请输入有效的运单号');
        return;
      }

      // 处理顺丰需要手机号的情况
      if (this.form.cpCode === 'SF') {
        try {
          const { value } = await this.$prompt('请输入收/发件人手机号码', '顺丰查询验证', {
            confirmButtonText: '开始查询',
            cancelButtonText: '取消',
            inputPattern: /^1\d{10}$/,
            inputErrorMessage: '请输入正确的11位手机号码'
          });
          this.form.phone = value;
        } catch {
          return; // 用户取消
        }
      }

      this.loading = true;
      this.data = []; // 清空旧数据
      
      // 设置展开状态
      if (this.isDataItemOpen) {
        this.activeNames = waybillList.map((_, i) => String(i));
      } else {
        this.activeNames = waybillList.length === 1 ? ['0'] : [];
      }

      // 顺序查询（避免接口限流）
      for (const waybill of waybillList) {
        await this.querySingle(this.form.cpCode, waybill);
      }
      
      this.loading = false;
    },

    // 单条查询
    async querySingle(cpCode, waybillNo) {
      try {
        const res = await this.apiService.post({
          modules: 'waybillRoute',
          content: {
            cpCode,
            waybillNo,
            phone: this.form.phone,
            sort: '1'
          }
        });

        if (res.status && res.data) {
          // 使用 unshift 保持查询顺序
          this.data.unshift(res.data);
        }
      } catch (err) {
        console.error(`查询 ${waybillNo} 失败:`, err);
      }
    },

    // 获取状态文本
    getStatusText(route) {
      if (!route || route.length === 0) return '未发出';
      const first = route[0];
      if (first.statusCode === 'COLLECT') return '已揽收';
      if (first.statusCode === 'SIGN' || first.text?.includes('已签收')) return '已签收';
      return '运输中';
    },

    // 获取状态颜色
    getStatusType(route) {
      if (!route || route.length === 0) return 'info';
      const first = route[0];
      if (first.statusCode === 'COLLECT') return 'warning';
      if (first.statusCode === 'SIGN' || first.text?.includes('已签收')) return 'success';
      return ''; // 默认蓝色
    },

    // 计算耗时
    timeConsuming(route) {
      if (!route || route.length === 0) return '0.00';
      
      const startTime = new Date(route[route.length - 1].time).getTime();
      let endTime = Date.now();
      
      // 已签收则用签收时间
      const first = route[0];
      if (first.statusCode === 'SIGN' || first.text?.includes('已签收')) {
        endTime = new Date(first.time).getTime();
      }
      
      const diff = Math.abs(endTime - startTime);
      return (diff / (1000 * 60 * 60)).toFixed(2);
    }
  },

  created() {
    // 支持 URL 参数带入查询
    const { cpCode, waybillNo } = this.$route.query;
    if (cpCode) this.form.cpCode = cpCode;
    if (waybillNo) {
      this.form.waybillNo = waybillNo;
      this.$nextTick(() => this.handleBatchQuery());
    }
  }
};
</script>

<style scoped>
.waybill-query-page {
  background-color: #f5f7fa;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
}

/* 搜索区域 */
.search-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.search-form .el-form-item {
  margin-bottom: 0;
  margin-right: 16px;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-top: 6px; /* 对齐输入框 */
}

/* 结果区域 */
.result-section {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  min-height: 60vh;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #909399;
  font-size: 14px;
}

.loading-wrapper i {
  margin-right: 8px;
  font-size: 18px;
}

/* 折叠面板样式 */
.waybill-collapse {
  border: none;
}

.collapse-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #303133;
  width: 100%;
}

.cp-name {
  font-weight: 600;
  color: #1d2129;
}

.waybill-no {
  color: #606266;
  font-family: monospace;
}

.status-tag {
  margin-left: 8px;
}

/* 表格微调 */
.weight-tag {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}
</style>